import { Component, DestroyRef, ElementRef, inject, OnInit, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { faBan, faCheck, faFileDownload, faFileUpload, faPen, faPlus, faTimes, faTrash } from '@fortawesome/free-solid-svg-icons';
import { NbToastrService } from '@nebular/theme';
import { ColumnModel, FilterSettingsModel, GridComponent, PageSettingsModel } from '@syncfusion/ej2-angular-grids';
import { forkJoin } from 'rxjs';
import * as XLSX from 'xlsx';
import { ErrorService } from '../../../../../@core/utils/error.service';
import { KmlService } from '../../../../../@core/utils/kml.service';
import { SessionService } from '../../../../../@core/utils/session.service';
import { ConfirmationData, ConfirmationDialogComponent } from '../../../../../partials/content/widgets/general/confirmation-dialog/confirmation-dialog.component';
import { Crop } from '../../../models/crop';
import { CropVariety, CropVarietyCategory } from '../../../models/crop-variety';
import { CropService } from '../../../services/crop.service';
import { VarietyService } from '../../../services/variety.service';
import { VarietiesDetailComponent } from '../varieties-detail/varieties-detail.component';
import { PlatformConfigService } from '../../../services/platform-config.service';
import { KwFilterDropdownService } from '../../../widgets/kw-filter-dropdown/kw-filter-dropdown.service';
@Component({
  selector: 'kw-varieties-list',
  templateUrl: './varieties-list.component.html',
  styleUrls: ['./varieties-list.component.scss']
})
export class VarietiesListComponent implements OnInit {
  private destroyRef = inject(DestroyRef);
  //#region Properties
  public cropLoading: boolean = true;
  public varietyList: CropVariety[] = [];
  public allVariety: CropVariety[] = [];
  public submitting: any;
  public allColumns: ColumnModel[] = [];
  public gridName: string = "varietiesConfigFilters";
  public pageSettings: PageSettingsModel = {
    pageSize: 50,
    pageSizes: [50, 100, 200],
  };
  public filterSettings: FilterSettingsModel = {
    mode: "Immediate",
    type: "Excel",
  };
  public toolbarOptions: string[] = [
    "ExcelExport",
  ];
  public enablePersistence: boolean = true;
  public filterCount: number = 0;

  displayedColumns = ['name', 'crop', 'code', 'integrationId', 'uiColor', 'active', 'edit'];
  //#endregion

  //#region Icon Properties
  public faPlus = faPlus;
  public faPen = faPen;
  public faTrash = faTrash;
  public faTimes = faTimes;
  public faCheck = faCheck;
  public faBan = faBan;
  public faFileDownload = faFileDownload;
  public faFileUpload = faFileUpload;
  public crops: Crop[] = [];
  public categories: CropVarietyCategory[] = [];
  public showRecheckInterval: boolean = false;
  public savingVarietyIds: Set<number> = new Set<number>();
  public highlightedVarietyId: number = null;
  private pendingScrollToVarietyId: number = null;
  //#endregion

  //#region ViewChild Properties

  @ViewChild("grid", { static: true }) grid: GridComponent;
  @ViewChild(MatPaginator, { static: false }) paginator: MatPaginator;
  @ViewChild(MatSort, { static: false }) sort: MatSort;

  @ViewChild("actionTemplate", { static: true })
  public actionTemplate: any;

  @ViewChild("isActiveTemplate", { static: true })
  public isActiveTemplate: any;

  @ViewChild("uiColorTemplate", { static: true })
  public uiColorTemplate: any;
  isLoading: boolean;
  //#endregion

  @ViewChild('fileInput') fileInput: ElementRef;

  constructor(
    private sessionService: SessionService,
    public dialogService: MatDialog,
    private errorService: ErrorService,
    private kmlService: KmlService,
    private toastrService: NbToastrService,
    private vrtService: VarietyService,
    private cropService: CropService,
    private platformConfigService: PlatformConfigService,
    public filterService: KwFilterDropdownService,
  ) { }

  ngOnInit() {
    this.cropLoading = true;

    this.vrtService.getCategories().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(x => {
      if (x.length > 0)
        this.displayedColumns = ['name', 'crop', 'code', 'integrationId', 'category', 'uiColor', 'active', 'edit'];
    })
    this.platformConfigService.getShowVarietyRecheckInterval().subscribe(show => {
      this.showRecheckInterval = show;
      this.setColumns();
    });
    this.loadAllVariety();
    this.loadData();
    this.GetCrop();
  }

  loadAllVariety(){
    this.vrtService.getVarieties(true).subscribe(vrts => {
      this.varietyList = (vrts || []).slice().sort((a, b) => {
        const an = a && a.name ? a.name.toString().toLowerCase() : '';
        const bn = b && b.name ? b.name.toString().toLowerCase() : '';
        return an.localeCompare(bn);
      });
      if (this.paginator) this.paginator._changePageSize(this.paginator.pageSize);
    })
  }

  loadData() {
    this.cropLoading = true;

    forkJoin([
      this.vrtService.getVarieties(),
    ]).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({next:
      (results) => {
        this.filterService.setVarieties(results[0] || []);
        this.allVariety = (results[0] || []).slice().sort((a, b) => {
          const an = a && a.name ? a.name.toString().toLowerCase() : '';
          const bn = b && b.name ? b.name.toString().toLowerCase() : '';
          return an.localeCompare(bn);
        });
        // Reuse this response to keep the kw-filter-dropdown variety cache in sync (no extra API call)
        this.filterService.setVarieties(results[0] || []);

        this.cropLoading = false;
        this.setColumns();
      },
      error:(err) => {
        this.errorService.handleError(err);
        this.cropLoading = false;
      }}
    );
  }

  toolbarClick(args) {
    this.cropLoading = true;
    this.grid.aggregates = [];
    this.grid.refresh();

    if (args.item.properties.text == "Excel Export") {
      this.grid
        .excelExport({
          fileName: `VarietiesExport.xlsx`,
        })
        .then(() => {
          this.cropLoading = false;
        });
    }
  }

  setColumns() {
    this.allColumns = [];

    // Hidden primary key column - lets us look up/select a specific row
    // (e.g. to scroll to and highlight a newly created variety).
    this.allColumns.push({
      field: "id",
      isPrimaryKey: true,
      visible: false,
    });

    this.allColumns.push(
      ...[
        {
          field: "name",
          headerText: "Name",
        },
        {
          field: "varietyShortName",
          headerText: "Code",
        },
        {
          field: "categoryName",
          headerText: "Category",
        },
        {
          field: "integrationId",
          headerText: "Integration Id",
        },
        {
          field: "cropName",
          headerText: "Crop",
        },
        {
          field: "uiColor",
          headerText: "Color",
          template: this.uiColorTemplate
        },
        {
          field: "isActive",
          headerText: "Is Active",
          template: this.isActiveTemplate
        },
        {
          headerText: "Action",
          template: this.actionTemplate
        },
      ]
    );

    if (this.showRecheckInterval) {
      this.allColumns.splice(this.allColumns.length - 1, 0, {
        field: "recheckInterval",
        headerText: "Recheck Interval",
      });
    }
  }

  confirmDelete(id: number) {
    const confirmDialogData: ConfirmationData = {
      title: "Confirm Deletion",
      confirmMessage: "Are you sure you want to delete this pool?",
      confirmLabel: "Delete",
      cancelLabel: "Cancel",
      displayComment: false,
      isWarning: false,
    };

    const confirmDialog = this.dialogService.open(ConfirmationDialogComponent, {
      width: "400px",
      data: confirmDialogData,
    });

    confirmDialog.afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe((result) => {
      if (result === true) {
        this.deleteRec(id);
      }
    });
  }

  deleteRec(id: number) {
    this.cropLoading = true;
    this.vrtService.deleteCropVariety(id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      {next:() => {
        this.toastrService.info("Verieties successfully deleted!");
        this.cropLoading = false;
        this.loadData();
      },
      error:(err) => {
        this.errorService.handleError(err);
        this.cropLoading = false;
      }}
    );
  }

  openDialog(IsEdit: boolean, data?: CropVariety) {
    const varietiesDetailsDialog = this.dialogService.open(VarietiesDetailComponent, {
      width: "800px",
      data: {
        isEditBtnClick: IsEdit
      }
    });
    if (IsEdit) {
      var persistData = (this.grid as GridComponent).getPersistData();
      window.localStorage.setItem("grid" + this.gridName, persistData);
      this.sessionService.selectedcropVarieties = { ...data };
    } else {
      window.localStorage.removeItem("grid" + this.gridName);
      this.sessionService.selectedcropVarieties = {} as CropVariety;
    }

    varietiesDetailsDialog.afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe((result) => {
      if (result && result.customFieldsOnly) {
        this.applyCustomFieldsOnly(result.id, result.customFields);
      } else if (result && result.backgroundSave && result.isNew && result.variety) {
        this.createVarietyInBackground(result.variety);
      } else if (result && result.backgroundSave && result.variety) {
        this.saveVarietyInBackground(result.variety);
      } else if (result) {
        this.loadData();
      }
      window.localStorage.removeItem("grid" + this.gridName);
      this.sessionService.selectedcropVarieties = {} as CropVariety;
    });
  }

  private applyCustomFieldsOnly(id: number, customFields: CropVariety['customFields']): void {
    const existing = this.allVariety.find(v => v.id === id);
    if (!existing) { return; }
    this.insertOrReplaceVarietyRow({ ...existing, customFields });
    this.refreshGridSoon();
  }

  private saveVarietyInBackground(variety: CropVariety): void {
    const originalVariety = this.allVariety.find(v => v.id === variety.id);
    this.savingVarietyIds.add(variety.id);
    this.insertOrReplaceVarietyRow(this.shapeVarietyForGrid(variety, originalVariety));
    this.filterService.upsertVariety(variety);
    this.refreshGridSoon();
    // Optimistically update the filter dropdown cache so other pages show the new value immediately
    this.filterService.upsertVariety(variety);

    this.vrtService.putCropVariety(variety).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (response) => {
        this.savingVarietyIds.delete(variety.id);
        // Endpoint is typed/behaves like a list-returning PUT for a single record - unwrap defensively.
        const updated: CropVariety = Array.isArray(response) ? response[0] : (response as unknown as CropVariety);
        const saved = { ...variety, ...(updated || {}) };
        this.filterService.upsertVariety(saved);
        // A crop change runs FixCropYears() server-side, which can create crop years the filter cache doesn't have
        if (originalVariety && originalVariety.cropId !== saved.cropId) {
          this.filterService.refreshCropYears();
        }
        const shaped = this.shapeVarietyForGrid(updated || variety, variety);
        this.insertOrReplaceVarietyRow(shaped);
        this.refreshGridSoon();
        this.toastrService.success(variety.name + ' successfully updated', variety.name + ' updated!');
      },
      error: (err) => {
        this.savingVarietyIds.delete(variety.id);
        if (originalVariety) {
          this.insertOrReplaceVarietyRow(originalVariety);
          this.filterService.upsertVariety(originalVariety);
        }
        this.refreshGridSoon();
        this.errorService.handleError(err);
        this.toastrService.danger('Failed to update "' + variety.name + '". Please try again.', "Save failed");
      }
    });
  }

  private createVarietyInBackground(variety: CropVariety): void {
    const tempId = -Date.now();
    const tempRow = this.shapeVarietyForGrid({ ...variety, id: tempId });
    this.insertOrReplaceVarietyRow(tempRow);
    this.filterService.upsertVariety(tempRow);
    this.savingVarietyIds.add(tempId);
    this.refreshGridSoon();
    // Optimistically add to the filter dropdown cache (swapped for the real id once saved)
    this.filterService.upsertVariety({ ...variety, id: tempId });

    this.vrtService.postCropVariety(variety).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (created) => {
        // Endpoint may behave like a list-returning POST for a single record - unwrap defensively (matches putCropVariety handling).
        const finalVariety: CropVariety = Array.isArray(created) ? created[0] : (created as unknown as CropVariety);
        this.filterService.removeVariety(tempId);
        this.filterService.upsertVariety(finalVariety || variety);
        this.savingVarietyIds.delete(tempId);
        this.allVariety = this.allVariety.filter(v => v.id !== tempId);
        this.filterService.removeVariety(tempId);
        if (finalVariety && finalVariety.id) {
          const shaped = this.shapeVarietyForGrid(finalVariety, variety);
          this.insertOrReplaceVarietyRow(shaped);
          this.filterService.upsertVariety(shaped);
          this.refreshGridSoon();
          this.jumpToVarietyRow(finalVariety.id);
        } else {
          // No usable record echoed back - reload so the grid and filter cache get the real id
          this.loadData();
        }
        this.toastrService.success(variety.name + ' successfully saved', 'Saved!');
      },
      error: (err) => {
        this.savingVarietyIds.delete(tempId);
        this.filterService.removeVariety(tempId);
        this.allVariety = this.allVariety.filter(v => v.id !== tempId);
        this.filterService.removeVariety(tempId);
        this.refreshGridSoon();
        this.errorService.handleError(err);
        this.toastrService.danger('Failed to create "' + (variety.name || 'variety') + '". Please try again.', "Create failed");
      }
    });
  }

  private isDefaultSortActive(): boolean {
    const cols = (this.grid && this.grid.sortSettings && this.grid.sortSettings.columns) || [];
    if (cols.length === 0) {
      return true;
    }
    return cols.length === 1 && cols[0].field === "name" && cols[0].direction === "Ascending";
  }

  private jumpToVarietyRow(id: number): void {
    this.highlightedVarietyId = id;
    // Scrolling happens once the grid actually finishes its next render
    // (see onGridDataBound) rather than after a guessed delay.
    this.pendingScrollToVarietyId = id;

    if (!this.grid || !this.isDefaultSortActive()) {
      return;
    }

    // Defer past the refresh(es) already queued by insertOrReplaceVarietyRow
    // so this pages using the grid's up-to-date data.
    setTimeout(() => {
      const index = this.allVariety.findIndex((v) => v.id === id);
      if (index === -1) {
        return;
      }

      const pageSize = this.pageSettings.pageSize || this.allVariety.length;
      const page = Math.floor(index / pageSize) + 1;

      if (this.grid.pageSettings.currentPage !== page) {
        this.grid.goToPage(page);
      }
    });
  }

  private scrollGridRowIntoView(row: Element): void {
    if (!row) {
      return;
    }
    const rowElement = row as HTMLElement;
    const scrollableContent = this.grid.getContent().querySelector(".e-content") as HTMLElement;
    if (scrollableContent) {
      const targetScrollTop = rowElement.offsetTop
        - (scrollableContent.clientHeight / 2)
        + (rowElement.offsetHeight / 2);
      scrollableContent.scrollTo({ top: Math.max(targetScrollTop, 0), behavior: "auto" });
    } else if (typeof rowElement.scrollIntoView === "function") {
      rowElement.scrollIntoView({ behavior: "auto", block: "center" });
    }
  }

  private startHighlightFade(row: Element, id: number): void {
    if (!row) { return; }
    const rowElement = row as HTMLElement;
    rowElement.classList.add("row-highlight-new");
    if (this.highlightedVarietyId === id) {
      this.highlightedVarietyId = null;
    }
    setTimeout(() => {
      rowElement.classList.remove("row-highlight-new");
    }, 2000);
  }

  private shapeVarietyForGrid(updated: CropVariety, original?: CropVariety): CropVariety {
    const crop = this.crops.find(c => c.id === updated.cropId);
    const category = this.categories.find(c => c.id === updated.categoryId);
    return {
      ...updated,
      cropName: crop ? crop.name : (updated.cropName || (original && original.cropName)),
      categoryName: category ? category.name : (updated.categoryName || (original && original.categoryName)),
      categoryDesignator: category ? category.designator : (updated.categoryDesignator || (original && original.categoryDesignator)),
      customFields: (updated.customFields && updated.customFields.length) ? updated.customFields : ((original && original.customFields) || updated.customFields)
    };
  }

  private insertOrReplaceVarietyRow(variety: CropVariety): void {
    const index = this.allVariety.findIndex(v => v.id === variety.id);
    const next = this.allVariety.slice();
    if (index !== -1) {
      next[index] = variety;
    } else {
      next.push(variety);
    }
    // Keep array order in sync with the grid's default alphabetical display
    // so jumpToVarietyRow's page-index math lands on the right page.
    next.sort((a, b) => {
      const an = a && a.name ? a.name.toString().toLowerCase() : '';
      const bn = b && b.name ? b.name.toString().toLowerCase() : '';
      return an.localeCompare(bn);
    });
    this.allVariety = next;
  }

  private refreshGridSoon(): void {
    setTimeout(() => {
      if (this.grid) {
        this.grid.refresh();
      }
    });
  }

  onRowDataBound(args: any): void {
    if (args.data && this.savingVarietyIds.has(args.data.id)) {
      args.row.classList.add('row-saving');
    } else if (args.row) {
      args.row.classList.remove('row-saving');
    }
    if (args.data && this.highlightedVarietyId != null && args.data.id === this.highlightedVarietyId) {
      args.row.classList.add('row-highlight-new');
    }
  }

  clearFilters() {
    window.localStorage.setItem("grid" + this.gridName, ""); // Here grid is component name and OrderDetails is component ID
    this.grid.clearFiltering();
    this.filterCount = 0;
  }

  onActionComplete(args: any) {
    if (args.requestType === 'filtering') {
      this.updateFilterCount();
    }
  }

  onGridDataBound() {
    this.updateFilterCount();

    if (this.pendingScrollToVarietyId != null) {
      const id = this.pendingScrollToVarietyId;
      const rowIndex = this.grid.getRowIndexByPrimaryKey(id);
      if (rowIndex > -1) {
        this.scrollGridRowIntoView(this.grid.getRowByIndex(rowIndex));
        this.pendingScrollToVarietyId = null;
        this.startHighlightFade(this.grid.getRowByIndex(rowIndex), id);

        // Something can quietly reset the content scroll a moment after
        // this - reassert it once more shortly after to counteract that.
        setTimeout(() => {
          const idx = this.grid.getRowIndexByPrimaryKey(id);
          if (idx > -1) {
            const row = this.grid.getRowByIndex(idx);
            this.scrollGridRowIntoView(row);
            this.startHighlightFade(row, id);
          }
        }, 200);
      }
    }
  }

  private updateFilterCount() {
    const cols = (this.grid && this.grid.filterSettings && this.grid.filterSettings.columns) ? this.grid.filterSettings.columns : [];
    const uniqueFields = cols.map(c => c.field as string).filter((f: string, i: number, a: string[]) => a.indexOf(f) === i);
    this.filterCount = uniqueFields.length;
  }

  downloadTemplate(): void {
    const workbook = XLSX.utils.book_new();
    const templateData = [
      {
        'Name*': '',
        'SHORT CODE': '',
        'Integration Name': '',
        'Color': '',
        'Organic': '',
        'Crop*': '',
        'Category': ''
      }];

    const worksheet = XLSX.utils.json_to_sheet(templateData, {
      header: ['Name*', 'SHORT CODE', 'Integration Name', 'Color', 'Organic', 'Crop*', 'Category']
    });
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Template');
    XLSX.writeFile(workbook, 'VarietiesTemplate.xlsx');
  }

  onFileSelected(event: any): void {
    const file = event.target.files[0];
    if (!file) { return; }

    this.cropLoading = true;
    const reader = new FileReader();

    reader.onload = (e: any) => {
      const data = new Uint8Array(e.target.result);
      const workbook = XLSX.read(data, { type: 'array' });

      const firstSheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[firstSheetName];
      const headerRows = XLSX.utils.sheet_to_json(worksheet, { header: 1, raw: true }) as any[][];
      const originalHeaders: string[] = (headerRows && headerRows.length > 0)
        ? headerRows[0].map((h: any) => (h == null ? '' : h.toString()))
        : [];
      const jsonData: any[] = XLSX.utils.sheet_to_json(worksheet, { raw: true });

      this.fileInput.nativeElement.value = '';

      const promises: Promise<any>[] = [];
      const seenNames = new Set<string>();

      jsonData.forEach(row => {

        // Validate Name
        if (!this.isValidName(row['Name*'])) {
          row['RESULT'] = 'Please Add Name.';
          return promises.push(Promise.resolve(row));
        }

        const varietyName = row['Name*'].toString().trim();
        const nameKey = varietyName.toLowerCase();

        if (seenNames.has(nameKey) ||
            this.varietyList.some(v => (v.name || '').trim().toLowerCase() === nameKey)) {
          row['RESULT'] = 'Duplicate';
          return promises.push(Promise.resolve(row));
        }

        const cropVariety = this.mapRowToCropVariety(row, null);

        // Validate Crop (falls back to default crop if the cell is blank)
        const cropResult = this.assignCrop(row, cropVariety);
        if (cropResult !== 'ok') {
          row['RESULT'] = cropResult === 'no-default'
            ? 'No default crop configured.'
            : 'Please Add Valid Crop.';
          return promises.push(Promise.resolve(row));
        }

        // Validate Category (optional)
        if (!this.assignCategory(row, cropVariety)) {
          row['RESULT'] = 'Please Add Valid Category.';
          return promises.push(Promise.resolve(row));
        }

        // NOTE: update-on-duplicate (PUT) path intentionally disabled.
        // Duplicates are rejected above. Revisit if we decide to allow
        // the import to toggle isActive on existing varieties.
        seenNames.add(nameKey);
        promises.push(
          this.vrtService.postCropVariety(cropVariety).toPromise()
            .then(() => {
              row['RESULT'] = 'Success';
              return row;
            })
            .catch(error => {
              row['RESULT'] = 'Error saving: ' + (error && error.message ? error.message : 'Unknown error');
              return row;
            })
        );
      });

      this.VarietiesImportResults(promises, originalHeaders);
    };

    reader.onerror = (error) => {
      this.errorService.handleError(error);
      this.cropLoading = false;
    };

    reader.readAsArrayBuffer(file);
  }

  private isValidName(name: any): boolean {
    return name != null && name.toString().trim() !== '';
  }

  private mapRowToCropVariety(row: any, existing: any): CropVariety {
    const obj: CropVariety = {
      name: row['Name*'] ? row['Name*'].toString().trim() : '',
      uiColor: row['Color'] ? row['Color'].toString().trim() : '',
      varietyShortName: row['SHORT CODE'] ? row['SHORT CODE'].toString().trim() : '',
      integrationId: row['Integration Name'] ? row['Integration Name'].toString().trim() : '',
      isOrganic: row['Organic'] ? this.toBoolean(row['Organic']) : false,
      isActive: true
    };

    // If update -> add ID
    if (existing) {
      obj.id = existing.id;
    }

    return obj;
  }

  private assignCrop(row: any, cv: CropVariety): 'ok' | 'no-default' | 'no-match' {
    if (!this.crops || this.crops.length === 0) { return 'no-default'; }

    const rawCrop = row['Crop*'] ? row['Crop*'].toString().trim() : '';

    if (!rawCrop) {
      const defaultCrop = this.crops.find(c => c.isDefault)
        || this.crops.find(c => (c.name || '').trim().toLowerCase() === 'default');
      if (!defaultCrop) { return 'no-default'; }
      cv.cropId = defaultCrop.id;
      return 'ok';
    }

    const match = this.crops.find(c => (c.name || '').toLowerCase().trim() === rawCrop.toLowerCase());
    if (!match) { return 'no-match'; }

    cv.cropId = match.id;
    return 'ok';
  }

  private assignCategory(row: any, cv: CropVariety): boolean {
    if (!this.categories || this.categories.length === 0) { return true; }

    if (!row['Category']) { return true; }

    const rowCat = row['Category'].toString().trim().toLowerCase();
    const match = this.categories.find(
      c => ((c.designator || '') + ' - ' + (c.name || '')).toLowerCase().trim() === rowCat
    );

    if (!match) { return false; }

    cv.categoryId = match.id;
    return true;
  }



  VarietiesImportResults(promises, originalHeaders: string[] = []) {
    // Wait for all operations to complete before generating Excel
    Promise.all(promises).then((results) => {
      results = this.sortExportResult(results)
      // Create the results workbook
      const resultWorkbook = XLSX.utils.book_new();
      const headerList = this.buildResultHeaders(originalHeaders, results);
      const resultSheet = XLSX.utils.json_to_sheet(results, { header: headerList });
      XLSX.utils.book_append_sheet(resultWorkbook, resultSheet, 'Varieties Report');
      XLSX.writeFile(resultWorkbook, 'VarietiesImportResults.xlsx');

      this.cropLoading = false;
      this.loadAllVariety();
      this.loadData(); // Refresh the grid
    }).catch(error => {
      console.error(error)
      this.errorService.handleError(error);
      this.cropLoading = false;
    });
  }

  private buildResultHeaders(originalHeaders: string[], results: any[]): string[] {
    const headers = (originalHeaders || []).filter(h => h && h !== 'RESULT');
    const seen = new Set(headers);
    (results || []).forEach(row => {
      Object.keys(row || {}).forEach(k => {
        if (k !== 'RESULT' && !seen.has(k)) { seen.add(k); headers.push(k); }
      });
    });
    headers.push('RESULT');
    return headers;
  }

  toBoolean(val: any): boolean {
    if (val === null || val === undefined) return false;

    // If it's already a number
    if (typeof val === "number") return val === 1;

    // Convert to string and trim safely
    const str = String(val).trim().toLowerCase().trim();
    return str === "yes" || str === "true" || str === "active" || str === "1";
  }


  GetCrop() {
    this.cropService.getCrops().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(crops => {
      this.crops = crops;
    });
    this.vrtService.getCategories().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(x => {
      this.categories = x;
    });

  }

  sortExportResult(list) {
    const resultPriority = {
      // Priority 1: Messages starting with "Error saving:"
      "Error saving:": 1,
      // Priority 2: Exact matches
      "Please Add Name.": 2,
      "Please Add Valid Crop.": 3,
      "No default crop configured.": 4,
      "Duplicate": 5,
      "Success": 6,
      // Default high number for unexpected messages (puts them last)
      "default": 99
    };

    return list.sort((a, b) => {
      const resultA = a.RESULT.trim();
      const resultB = b.RESULT.trim();

      // Helper function to get priority based on exact match or startsWith check
      const getPriority = (resultMsg) => {
        // Check for the highest priority message type first
        if (resultMsg.startsWith("Error saving:")) {
          return resultPriority["Error saving:"];
        }

        // Check for exact matches
        return resultPriority[resultMsg] || resultPriority.default;
      };

      const priorityA = getPriority(resultA);
      const priorityB = getPriority(resultB);

      // Standard comparison logic:
      // priorityA < priorityB returns a negative number, so 'a' comes before 'b'
      return priorityA - priorityB;
    });
  }
  downloadFiles(args:string){
    this.cropLoading = true;
    this.grid.aggregates = [];
    this.grid.refresh();

    if (args.includes("Excel")) {
      this.grid
        .excelExport({
          fileName: `VarietiesExport.xlsx`,
        })
        .then(() => {
          this.cropLoading = false;
        });
    }
  }

}
